import { useRef, type KeyboardEvent, type ReactNode } from 'react';
import { useSearchParams } from 'react-router-dom';
import { Badge } from './StatusPill.js';
import { ScrollStrip } from './ScrollStrip.js';
import { SelectField } from './Field.js';
import { formatBadgeCount } from './format.js';
import { useIsMobile } from './use-media.js';

export interface TabItem<Id extends string = string> {
  id: Id;
  label: string;
  count?: number;
  disabled?: boolean;
}

export interface TabsProps<Id extends string> {
  /** Stable prefix for tab and panel ids; `TabPanel` takes the same value. */
  id: string;
  label: string;
  tabs: readonly TabItem<Id>[];
  value: Id;
  onChange: (next: Id) => void;
  /** Above this many tabs, a phone gets a select instead of a strip (03-components §8). */
  mobileSelectThreshold?: number;
  className?: string;
}

export function tabDomId(prefix: string, tabId: string): string {
  return `${prefix}-tab-${tabId}`;
}

export function panelDomId(prefix: string, tabId: string): string {
  return `${prefix}-panel-${tabId}`;
}

/**
 * In-page tabs for views of equal standing (03-components §8) — never navigation between pages,
 * which is the SectionStrip. Automatic activation on arrow keys, Home/End, one tab in the tab
 * order; selection lives in the URL through `useUrlTab`. Counts stay on the tab at every width.
 */
export function Tabs<Id extends string>({ id, label, tabs, value, onChange, mobileSelectThreshold = 5, className }: TabsProps<Id>) {
  const isMobile = useIsMobile();
  const listRef = useRef<HTMLDivElement | null>(null);

  if (isMobile && tabs.length > mobileSelectThreshold) {
    return (
      <div className={['tabs', 'tabs--select', className].filter(Boolean).join(' ')}>
        <SelectField
          label={label}
          hideLabel
          value={value}
          onChange={(event) => onChange(event.target.value as Id)}
          options={tabs.map((tab) => ({ value: tab.id, label: tab.count !== undefined ? `${tab.label} (${tab.count})` : tab.label, disabled: tab.disabled }))}
        />
      </div>
    );
  }

  const enabled = tabs.filter((tab) => !tab.disabled);
  const onKeyDown = (event: KeyboardEvent<HTMLDivElement>): void => {
    if (event.nativeEvent.isComposing) return;
    const index = enabled.findIndex((tab) => tab.id === value);
    let next: TabItem<Id> | undefined;
    switch (event.key) {
      case 'ArrowRight':
        next = enabled[(index + 1) % enabled.length];
        break;
      case 'ArrowLeft':
        next = enabled[(index - 1 + enabled.length) % enabled.length];
        break;
      case 'Home':
        next = enabled[0];
        break;
      case 'End':
        next = enabled[enabled.length - 1];
        break;
      default:
        return;
    }
    if (!next) return;
    event.preventDefault();
    onChange(next.id);
    listRef.current?.querySelector<HTMLElement>(`#${CSS.escape(tabDomId(id, next.id))}`)?.focus({ preventScroll: true });
  };

  return (
    <div className={['tabs', className].filter(Boolean).join(' ')}>
      <ScrollStrip selectedKey={value}>
        <div ref={listRef} role="tablist" aria-label={label} className="tabs__list" onKeyDown={onKeyDown}>
          {tabs.map((tab) => {
            const selected = tab.id === value;
            return (
              <button
                key={tab.id}
                type="button"
                role="tab"
                id={tabDomId(id, tab.id)}
                aria-selected={selected}
                aria-controls={panelDomId(id, tab.id)}
                aria-disabled={tab.disabled || undefined}
                tabIndex={selected ? 0 : -1}
                className={['tabs__tab', selected ? 'is-selected' : '', tab.disabled ? 'is-disabled' : ''].filter(Boolean).join(' ')}
                onClick={() => { if (!tab.disabled) onChange(tab.id); }}
              >
                <span className="tabs__label">{tab.label}</span>
                {tab.count !== undefined ? <Badge tone={selected ? 'accent' : 'neutral'} label={`${tab.count} in ${tab.label}`}>{formatBadgeCount(tab.count)}</Badge> : null}
              </button>
            );
          })}
        </div>
      </ScrollStrip>
    </div>
  );
}

export interface TabPanelProps {
  tabsId: string;
  tabId: string;
  active: boolean;
  children: ReactNode;
  className?: string;
  /**
   * The panel's own heading, rendered as a visually-hidden `<h2>` before the content. Why: the tab
   * strip is not a heading, so sections inside a panel (`<h3>`) would otherwise sit directly under
   * the page's `<h1>` — a skipped level (01-foundations §7.3). Pass the tab's label.
   */
  heading?: string;
}

/**
 * The panel for one tab; inactive panels are not rendered, so nothing hidden is loaded. It is in the tab order
 * (`tabIndex={0}`), so its keyboard focus ring is drawn by `.tabs__panel:focus-visible` (components.css) with
 * `--focus-ring` — 01-foundations §7.2 — never removed without it.
 */
export function TabPanel({ tabsId, tabId, active, children, className, heading }: TabPanelProps) {
  if (!active) return null;
  return (
    <div role="tabpanel" id={panelDomId(tabsId, tabId)} aria-labelledby={tabDomId(tabsId, tabId)} tabIndex={0} className={['tabs__panel', className].filter(Boolean).join(' ')}>
      {heading ? <h2 className="visually-hidden">{heading}</h2> : null}
      {children}
    </div>
  );
}

/**
 * Tab selection in the URL (`?tab=`), so it survives refresh and back-navigation (06-modules §5.5).
 * Unknown values fall back to the default. Changes replace history, consistently across the app.
 */
export function useUrlTab<Id extends string>(allowed: readonly Id[], fallback: Id, param = 'tab'): [Id, (next: Id) => void] {
  const [params, setParams] = useSearchParams();
  const raw = params.get(param);
  const value = raw !== null && (allowed as readonly string[]).includes(raw) ? (raw as Id) : fallback;
  const set = (next: Id): void => {
    const copy = new URLSearchParams(params);
    if (next === fallback) copy.delete(param);
    else copy.set(param, next);
    setParams(copy, { replace: true });
  };
  return [value, set];
}
